import { feed as libraryFeed, IFeedConfiguration } from 'feed';
import {
  KEY_FEED_EVENT_CATEGORIES_SESSION_STORAGE,
  KEY_FEED_EVENT_PRODUCTS_SESSION_STORAGE,
  KEY_FEED_SESSION_STORAGE,
  KEY_FOR_ANY_FEED_EVENT_SESSION_STORAGE,
} from '~/actions/feed/constants';
import { ISomeIdList } from '~/actions/feed/utils/feedApi/types';
import { addClick } from '~/actions/feed/utils/feedLocaleStorageControl';
import RestServices from '~/api';
import { getFromSessionStorage, saveToSessionStorage } from '~/helpers/storage';
import EventTransportService from '~/listenerEventService/EventTransportService';
import { clickstreamLogger } from '~/logging';
import settings from '~/utils/Settings';
import { clearingUrlFromHash } from '~/utils/clearingUrlFromHash';
import { getCurrentPlatformType } from '~/utils/getCurrentPlatformType';

export const getKeyForStorage = (recommendationGenerationType: RecommendationGenerationType) => {
  return recommendationGenerationType === 'BY_CATEGORY'
    ? KEY_FEED_EVENT_CATEGORIES_SESSION_STORAGE
    : KEY_FEED_EVENT_PRODUCTS_SESSION_STORAGE;
};

/**
 * Сравнивает текущую платформу с заданной в ленте
 * @param feedPlatformType - тип устройства на котором должна отобразиться лента
 * @returns true / false
 */
const checkPlatform = (feedPlatformType: string[], feedId: string): boolean => {
  const checkResult = feedPlatformType.includes(getCurrentPlatformType());
  if (!checkResult) {
    clickstreamLogger.warn(
      `Feed: ${feedId} - the feed device does not match the current device`
    );
  }

  return checkResult;
};

/**
 * Возвращает массив типов ленты исходя из селекторов
 * @param selector
 * @param selectorMobile
 */
const computeFeedPlatformType = (
  selector: string,
  selectorMobile: string
): string[] => {
  const feedPlatformType: string[] = [];
  if (selector) feedPlatformType.push('desktop');
  if (selectorMobile) feedPlatformType.push('mobile');

  return feedPlatformType;
};

/**
 * Получает список лент из SessionStorage
 * @returns Array: IFeedList
 */
export const getFeeds = (): Feed[] => {
  const feeds: IFeedList = JSON.parse(
    JSON.stringify(getFromSessionStorage(KEY_FEED_SESSION_STORAGE, 'feedList'))
  );
  if (feeds) return feeds.payload;

  return [];
};

/**
 * Сопоставляет текущий адрес страницы без / в конце (если он есть) с urlPattern-ном
 * если urlPattern не задан или задан без {{}}, возвращает true
 * @param urlPattern
 * @returns
 */
const checkUrlPattern = (urlPattern: string, feedId: string): boolean => {
  const checkUrlPatternResult =
    urlPattern?.startsWith('{{') && urlPattern?.endsWith('}}')
      ? new RegExp(urlPattern.substring(2, urlPattern.length - 2)).test(
        location.href.replace(/\/$/, '')
      )
      : true;
  if (!checkUrlPatternResult) {
    clickstreamLogger.warn(
      `Feed: ${feedId} - the current page does not match the parameter urlPattern`
    );
  }

  return checkUrlPatternResult;
};

/**
 * Возвращает селектор, актуальный для текущей платформы
 * @param feed: Feed - объект с параметрами ленты
 * @returns
 */
export const getActualSelector = (feed: Feed): string => {
  return getCurrentPlatformType() === 'mobile'
    ? feed.selectorMobile
    : feed.selector;
};

/**
 * Удаляет ленту из отложенной очереди SberLeadDeferredQueueFeed
 * @param feedId 
 */
const removeFeedFromDeferredQueueFeed = (feedId: string) => {
  const removeFeedIndex = window.SberLeadDeferredQueueFeed.findIndex(
    feed => feed.feedId === feedId
  );
  if (removeFeedIndex !== -1) {
    window.SberLeadDeferredQueueFeed.splice(removeFeedIndex, 1);
    clickstreamLogger.warn(`Feed: ${feedId} - removed from queue: SberLeadDeferredQueueFeed`);
  }
};

/**
 * Отправляет feedId ленты
 */
const feedShowEvent = (feedId: string, recType: RecType): void => {
  // Не отправляем событие, если включен демо-режим
  if (window?.clickstreamAnalyticUtilitiesDemo?.isDemo) return;
  EventTransportService.send({
    type: 'feed-show-event',
    feedId,
    recType,
  });
  RestServices.feedEvent.set({
    '@type': 'FeedShowEvent',
    feedId,
  });
};

/**
 * Отправляет feedId и price при клике на рекомендацию и редирект
 * @param feedId 
 * @param recType 
 * @param productId 
 * @param price 
 * @param urlLink 
 * @param isRedirect 
 */
const feedCardClickEvent = (
  feedId: string,
  recType: RecType,
  productId: string,
  price: number,
  urlLink: string,
  isRedirect: boolean = true
): void => {
  const redirect = () => {
    if (isRedirect) location.href = urlLink;
  };
  // Не отправляем событие, если включен демо-режим
  if (window?.clickstreamAnalyticUtilitiesDemo?.isDemo) {
    redirect();

    return;
  }
  EventTransportService.send({
    type: 'feed-recommendation-click-event',
    feedId,
    recType,
    productId,
    price,
  });
  addClick(productId, feedId, recType);
  RestServices.feedEvent
    .set({
      '@type': 'FeedRecommendationClickEvent',
      feedId,
      price,
    })
    .finally(redirect);
};

/**
 * Запускает процесс отображения ленты
 * @param feed: Feed - объект с параметрами ленты
 * @param cards
 */
const showFeed = (feed: Feed, cards: IFeedCardAndCategory) => {
  // Если лента есть на странице, но рекомендаций нет - удаляем ленту
  const hasFeedInPage = libraryFeed.FeedCreator.hasFeedInPage(feed.feedId);
  if (hasFeedInPage && !cards.recommendations?.length) {
    libraryFeed.killFeed(feed.feedId);

    return;
  }
  // Добавим условный id в рекомендации в которых его нет (актуально для ЛЛ)
  const recommendations = cards.recommendations.map((card, i) => {
    if (!card.id) card.id = `csa_temp_id_${i}`;

    return card;
  });
  // Создаем класс ленты
  const feedFromLibrary = new libraryFeed.FeedCreator(
    feed.feedId,
    getActualSelector(feed),
    feed.configuration as IFeedConfiguration,
    recommendations,
    getCurrentPlatformType() === 'mobile',
    cards.categories,
    cards?.categories[0]?.categoryId ?? ''
  );
  // Добавляем функции обратного вызова на клики
  const { clickFunctionName } = feed.configuration;
  let callbackFunctionForButton;
  if (clickFunctionName &&
    // @ts-ignore
    typeof (window[clickFunctionName]) === 'function'
  ) {
    callbackFunctionForButton = ({ element }: { element: HTMLDivElement }) => {
      const productId = element.dataset.product;
      // @ts-ignore
      const newButtonName = window[clickFunctionName](productId, element);
      if (newButtonName && typeof newButtonName === 'string') {
        element.innerHTML = newButtonName;
      }
    };
  }
  feedFromLibrary.setCallbacks({
    callbackForCardsClick: ({ element, event }: { element: HTMLDivElement, event: MouseEvent }) => {
      const productId = element.dataset.product;
      const currentRecommendations = recommendations.find(
        card => `${card.id}` === productId
      );
      const { price, urlLink } = currentRecommendations;
      feedCardClickEvent(
        feed.feedId,
        feed.recType,
        productId,
        price,
        urlLink,
        event.button === 0
      );
    },
    callbackForCardButtonClick: callbackFunctionForButton,
  });
  // Вызываем метод рендеринга в зависимости от наличия ленты на странице
  const isFeedRendered: boolean = hasFeedInPage
    ? feedFromLibrary.refresh()
    : feedFromLibrary.start();
  // Логируем рендер, удаляем ленту из отложенной очереди, вызываем событие показа ленты 
  if (isFeedRendered) {
    clickstreamLogger.warn(`Feed: ${feed.feedId} - successfully rendered`);
    removeFeedFromDeferredQueueFeed(feed.feedId);
    feedShowEvent(feed.feedId, feed.recType);

    return;
  }
  // Логируем ошибку рендеринга
  clickstreamLogger.warn(`Feed: ${feed.feedId} - error rendered`);
};

/**
 * Получает данные из сессии для получения рекомендаций по someIdList
 * @param externalKey 
 * @returns 
 */
const getSomeIdListFromStorage = (feed: Feed): {
  someIdInStorage: ISomeIdList;
  updateSessionFlag: boolean;
  status: boolean;
} => {
  let someIdInStorage: ISomeIdList = getFromSessionStorage(
    getKeyForStorage(feed.recommendationGenerationType),
    feed.externalKey
  );
  let updateSessionFlag: boolean = true;
  let status: boolean = false;
  if (!someIdInStorage) {
    someIdInStorage = getFromSessionStorage(
      getKeyForStorage(feed.recommendationGenerationType),
      KEY_FOR_ANY_FEED_EVENT_SESSION_STORAGE
    );
    updateSessionFlag = false;
  }
  if (
    someIdInStorage &&
    someIdInStorage?.url === window.location.href &&
    someIdInStorage?.someIdList.length > 0
  ) status = true;

  return { someIdInStorage, updateSessionFlag, status };
};

/**
 * Добавляет ленту в отложенную очередь SberLeadDeferredQueueFeed
 * @param feed 
 */
const setDeferredQueueFeed = (feed: Feed) => {
  if (!window.SberLeadDeferredQueueFeed.find(
    feedInDeferredQueue => feed.feedId === feedInDeferredQueue.feedId
  )) {
    window.SberLeadDeferredQueueFeed.push(feed);
    clickstreamLogger.warn(`Feed: ${feed.feedId} - added to the queue: SberLeadDeferredQueueFeed`);
  }
};

/**
 * Проверяет флаг FEED_USE_DYNAMIC_SELECTOR_OBSERVER
 * активирующий слушателя MutationObserver
 * @returns boolean
 */
const isActiveDynamicSelectorObserver = (): boolean => {
  return settings.getOrDefault(
    'FEED_USE_DYNAMIC_SELECTOR_OBSERVER',
    false
  );
};

/**
 * Проверяет наличие селектора на странице
 * @param selector
 * @returns
 */
const hasSelectorOnPage = (feed: Feed): boolean => {
  const isSelectorFound = document.querySelector(getActualSelector(feed)) ? true : false;
  if (!isSelectorFound) {
    clickstreamLogger.warn(`Feed: ${feed.feedId} - selector not found`);
  }

  return isSelectorFound;
};

/**
 * Проверяет условия показа ленты:
 * 1. Проверка платформы - desktop | mobile
 * 2. Проверка страницы - urlPattern
 * 3. Проверка наличия селектора на странице
 *
 * @param feed: Feed - объект с параметрами ленты
 */
export const checkConditions = (feed: Feed): boolean => {
  if (
    checkPlatform(
      computeFeedPlatformType(feed.selector, feed.selectorMobile),
      feed.feedId
    ) &&
    checkUrlPattern(feed.urlPattern, feed.feedId) &&
    hasSelectorOnPage(feed)
  ) return true;

  return false;
};

/**
 * Отправляет запрос рекомендаций по url
 * Если рекомендации вернулись вызывает метод showFeed
 * @param feed 
 */
const fetchRecommendationsByUrl = (feed: Feed) => {
  RestServices.feedCards.get({ feedId: feed.feedId, urlLink: clearingUrlFromHash(location.href) })
    .then(({ payload }: IFeedDetail) => {
      if (payload[0] && payload[0].recommendations.length) {
        showFeed(feed, payload[0]);

        return;
      }
      clickstreamLogger.warn(`Feed: ${feed.feedId} - проблемы при загрузке рекомендаций, метод fetchRecommendationsByUrl`);
    });
};

/**
 * Отправляет запрос рекомендаций по SomeId
 * Если рекомендации вернулись вызывает метод showFeed
 * @param feed 
 * @param someIdList 
 * @param updateSessionFlag 
 */
const fetchRecommendationsBySomeId = (feed: Feed, someIdList: string, updateSessionFlag: boolean) => {
  RestServices.feedCards.get({ feedId: feed.feedId, someIdList })
    .then(({ payload }: IFeedDetail) => {
      if (payload[0] && payload[0].recommendations.length) {
        showFeed(feed, payload[0]);
        if (updateSessionFlag) {
          saveToSessionStorage(getKeyForStorage(feed.recommendationGenerationType), feed.externalKey, {
            url: window.location.href,
            someIdList: [],
          });
        }
        
        return;
      }
      clickstreamLogger.warn(`Feed: ${feed.feedId} - проблемы при загрузке рекомендаций, метод fetchRecommendationsBySomeId`);
    });
};

/**
 * Инициирует запрос рекомендаций по url или по someId
 * 
 * @param feed: Feed - объект с параметрами ленты
 */
export const getRecommendations = (feed: Feed) => {
  if (checkConditions(feed)) {
    const { someIdInStorage, updateSessionFlag, status } = getSomeIdListFromStorage(feed);
    if (status) {
      fetchRecommendationsBySomeId(feed, JSON.stringify(someIdInStorage.someIdList), updateSessionFlag);

      return;
    }
    if (feed.recommendationGenerationType !== 'BY_CATEGORY') {
      fetchRecommendationsByUrl(feed);

      return;
    }
    clickstreamLogger.warn(
      `Feed: ${feed.feedId} - недостаточно данных для отображения ленты BY_CATEGORY`
    );
  }
};

/**
 * Пробегает по списку лент в сессии и пытается каждую нарисовать
 */
export const checkFeedListInSessionStorage = (): void => {
  getFeeds().forEach((feed) => {
    getRecommendations(feed);
  });
};

/**
 * Находит ленты, которые нужно добавить в отложенную очередь и добавляет их в нее
 */
const findRemoteFeed = (feeds: Feed[]) => {
  clickstreamLogger.warn(`Feed - find remote feed`);
  feeds
    .filter(feed => checkPlatform(
      computeFeedPlatformType(feed.selector, feed.selectorMobile),
      feed.feedId
    ))
    .filter(feed => checkUrlPattern(feed.urlPattern, feed.feedId))
    .filter(feed => !hasSelectorOnPage(feed))
    .forEach((feed) => {
      setDeferredQueueFeed(feed);
    });
};

/**
 * Запускает ленты из отложенной очереди SberLeadDeferredQueueFeed
 */
const restartDeferredQueueFeed = (feeds: Feed[]) => {
  findRemoteFeed(feeds);
  if (window.SberLeadDeferredQueueFeed.length) {
    window.SberLeadDeferredQueueFeed.forEach((feed) => {
      clickstreamLogger.warn(`Feed: ${feed.feedId} - rendering attempt`);
      getRecommendations(feed);
    });
  }
};

/**
 * Включает функцию динамического селектора
 */
export const includesDynamicSelectorObserver = (feeds: Feed[]): void => {
  if (isActiveDynamicSelectorObserver()) {
    clickstreamLogger.warn(`Feed: start dynamic selector observer`);
    restartDeferredQueueFeed(feeds);
    const observer = new MutationObserver(() => restartDeferredQueueFeed(feeds));
    observer.observe(
      document,
      {
        childList: true,
        subtree: true,
      }
    );
  }
};

/**
 * Активирует ленту
 * Добавляет слушатель на изменение адресной строки
 * Запускает проверку всех лент
 */
const activeFeed = (): void => {
  includesDynamicSelectorObserver(getFeeds());
  window.addEventListener('popstate', checkFeedListInSessionStorage);
  checkFeedListInSessionStorage();
};

/**
 * Получает список запущенных лент и записывает данные в SessionStorage
 * Активируем ленты, если данные есть
 */
export const setFeedList = (): void => {
  RestServices.feedList.get('ACTIVE').then((result: IFeedList) => {
    saveToSessionStorage(KEY_FEED_SESSION_STORAGE, 'feedList', result);
    if (result.payload.length > 0) {
      activeFeed();
    }
  });
};

/**
 * Инициализирует ленту при загрузке страницы
 * Проверяет данные в SessionStorage,
 * Если данных нет - получает список лент,
 * Если данные есть - активирует ленты
 */
export const initFeed = (): void => {
  if (getFromSessionStorage(KEY_FEED_SESSION_STORAGE, 'feedList')) {
    if (getFeeds().length > 0) {
      activeFeed();
    }

    return;
  }
  setFeedList();
};
